import type {ReactElement} from 'react';
import React from 'react';
import {POSTER_MAX_DURATION, POSTER_MIN_DURATION} from '@PosterWhiteboard/poster/poster.types';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {BaseDownloadDuration} from '@Components/download-panel/components/base-download-duration/base-download-duration';

export function DownloadVideoDurationOption(): ReactElement {
  const {duration, setDuration, originalDuration} = useDownloadPanelData();
  const onDurationChanged = (value: number): void => {
    setDuration(value);
  };

  const onSlide = (value: number): void => {
    setDuration(getExponentialFunctionBackwards(value));
  };

  const VALUE_BREAKPOINT = 60;
  const SLIDER_BREAKPOINT_PERCENTAGE = 0.9;

  const VALUE_RANGE_FIRST_PART = VALUE_BREAKPOINT - POSTER_MIN_DURATION;
  const VALUE_RANGE_SECOND_PART = POSTER_MAX_DURATION - VALUE_BREAKPOINT;
  const SLIDER_TRACK_RANGE = POSTER_MAX_DURATION - POSTER_MIN_DURATION;
  const SLIDER_BREAKPOINT = POSTER_MIN_DURATION + SLIDER_TRACK_RANGE * SLIDER_BREAKPOINT_PERCENTAGE;
  const SLIDER_RANGE_FIRST_PART = SLIDER_BREAKPOINT - POSTER_MIN_DURATION;
  const SLIDER_RANGE_SECOND_PART = POSTER_MAX_DURATION - SLIDER_BREAKPOINT;

  const getExponentialFunctionForwards = (val: number): number => {
    const clampedVal = Math.max(POSTER_MIN_DURATION, Math.min(val, POSTER_MAX_DURATION));

    if (clampedVal <= VALUE_BREAKPOINT) {
      const percentage = (clampedVal - POSTER_MIN_DURATION) / VALUE_RANGE_FIRST_PART;
      return POSTER_MIN_DURATION + percentage * SLIDER_RANGE_FIRST_PART;
    } else {
      const percentage = (clampedVal - VALUE_BREAKPOINT) / VALUE_RANGE_SECOND_PART;
      return SLIDER_BREAKPOINT + percentage * SLIDER_RANGE_SECOND_PART;
    }
  };

  const getExponentialFunctionBackwards = (sliderPos: number): number => {
    const clampedSliderPos = Math.max(POSTER_MIN_DURATION, Math.min(sliderPos, POSTER_MAX_DURATION));

    if (clampedSliderPos <= SLIDER_BREAKPOINT) {
      const percentage = (clampedSliderPos - POSTER_MIN_DURATION) / SLIDER_RANGE_FIRST_PART;
      const val = POSTER_MIN_DURATION + percentage * VALUE_RANGE_FIRST_PART;
      return val;
    } else {
      const percentage = (clampedSliderPos - SLIDER_BREAKPOINT) / SLIDER_RANGE_SECOND_PART;
      const val = VALUE_BREAKPOINT + percentage * VALUE_RANGE_SECOND_PART;
      return val;
    }
  };

  return (
    <BaseDownloadDuration
      marks={[getExponentialFunctionForwards(originalDuration)]}
      duration={duration}
      onDurationChanged={onDurationChanged}
      originalDuration={originalDuration}
      sliderValue={getExponentialFunctionForwards(duration)}
      onSlide={onSlide}
      min={POSTER_MIN_DURATION}
      max={POSTER_MAX_DURATION}
    />
  );
}
